import { formatoPrecio } from '@/lib/utils/formato-precio';
import type { Cruce, RangoDeRegla, SalidasSinPrecio } from '../types/pricing-rules.types';
import { COMO_SE_COMBINAN } from '../lib/jerarquia';

/**
 * La barra de rangos (spec 208 §2, maqueta S0 y S8). Cada regla que alcanza al
 * producto es una franja: el tramo de precios que permite. Donde se encima con
 * todas las demás está el cruce: ahí se cumplen todas. La estrategia pide un
 * precio; queda el punto del cruce más cercano.
 */

/**
 * Las marcas sobre el eje. `queda` es lo que se SIRVE (`precioFinal`, o el precio
 * escrito a mano); la propuesta (`precioPropuesto`) no es una marca: cuando es
 * distinta, la pantalla la dice en texto. `costo` es una raya fina de referencia.
 */
export type Marcas = {
  hoy?: number | null;
  pedido?: number | null;
  queda?: number | null;
  costo?: number | null;
};

/**
 * El formato único del precio vive en `@/lib/utils/formato-precio` (pulido,
 * spec 232). Se re-exporta desde acá para no mover los 15 lugares que ya lo
 * importaban de este archivo.
 */
export { formatoPrecio };
const fmt = formatoPrecio;

// Un margen para el error de coma flotante: 1.1 * 100 = 110.00000000000001 no
// es «más de 110 centavos».
const CASI_CERO = 1e-6;

/**
 * El piso de un rango, impreso: al centavo, hacia ARRIBA. Un piso de 5.415,583
 * redondeado al más cercano se leía «5.415,58», un precio que no cumple la regla
 * (revisión final, B4). Con «5.415,59» el número que se ve sí la cumple.
 */
export const pisoEnTexto = (n: number) => formatoPrecio(Math.ceil(n * 100 - CASI_CERO) / 100);

/** El techo de un rango, impreso: al centavo, hacia ABAJO (lo mismo que el piso, al revés). */
export const techoEnTexto = (n: number) => formatoPrecio(Math.floor(n * 100 + CASI_CERO) / 100);

export function ejeDe(rangos: RangoDeRegla[], marcas: Marcas): { desde: number; hasta: number } {
  const puntos: number[] = [];
  for (const r of rangos) {
    if (r.min != null) puntos.push(r.min);
    if (r.max != null) puntos.push(r.max);
  }
  for (const m of [marcas.hoy, marcas.pedido, marcas.queda, marcas.costo])
    if (m != null) puntos.push(m);
  if (puntos.length === 0) return { desde: 0, hasta: 1 };
  const lo = Math.min(...puntos);
  const hi = Math.max(...puntos);
  // Aire a los costados: 8 % del tramo, o 10 % del valor si todo es un punto.
  const aire = hi > lo ? (hi - lo) * 0.08 : Math.max(Math.abs(lo) * 0.1, 1);
  return { desde: Math.max(0, lo - aire), hasta: hi + aire };
}

/**
 * Las etiquetas de arriba de la barra («hoy», «pide», «costo») en filas que no se
 * pisan (B1 de la revisión final): se ordenan por posición en el eje (0..1) y cada
 * una va en la fila más baja cuya última etiqueta quede a `distanciaMinima` o más.
 * Fila 0 es la pegada a la barra. Con precios cercanos —el caso normal: lo que pide
 * la estrategia y el precio de hoy suelen estar a pocos puntos— salen escalonadas.
 */
export function filasDeEtiquetas(
  marcas: Array<{ clave: string; pos: number }>,
  distanciaMinima = 0.1
): Record<string, number> {
  const ultimaPorFila: number[] = [];
  const filas: Record<string, number> = {};
  for (const m of [...marcas].sort((a, b) => a.pos - b.pos)) {
    let fila = ultimaPorFila.findIndex((ultima) => m.pos - ultima >= distanciaMinima);
    if (fila === -1) fila = ultimaPorFila.length;
    ultimaPorFila[fila] = m.pos;
    filas[m.clave] = fila;
  }
  return filas;
}

/** Alto de una fila de etiquetas, en px (`text-xs` con su interlineado). */
const ALTO_FILA = 16;

/**
 * Lote A del 2-oct (A5): un rótulo centrado sobre una raya pegada al borde se
 * salía del dibujo («3.360,» cortado). Cerca de un borde se ancla hacia adentro.
 * `pos` va de 0 a 1 sobre el eje.
 */
export function anclaDelRotulo(pos: number): string {
  if (pos < 0.1) return 'translate-x-0';
  if (pos > 0.9) return '-translate-x-full';
  return '-translate-x-1/2';
}

/**
 * Lote A del 2-oct (A5): por debajo de esta fracción del eje, el tramo verde es
 * muy angosto para «3.440,42 – …» adentro: los números van al costado.
 */
const CRUCE_ANGOSTO = 0.2;

/**
 * Lo que va al lado del nombre de la regla: su alcance, salvo que el nombre ya
 * termine con él (los nombres automáticos lo llevan: «No ganar menos de 20 % · Sony»).
 */
export function sufijoDeAlcance(ruleName: string, alcance: string | undefined): string | null {
  if (!alcance) return null;
  return ruleName.trimEnd().endsWith(alcance) ? null : alcance;
}

export function BarraDeRangos({
  rangos,
  cruce,
  marcas,
  salidas = null,
  compacta = false,
  alcancePorRegla = {},
  pie,
}: {
  rangos: RangoDeRegla[];
  cruce: Cruce;
  marcas: Marcas;
  salidas?: SalidasSinPrecio | null;
  compacta?: boolean;
  alcancePorRegla?: Record<string, string>;
  /**
   * Lote A del 2-oct (A5): lo que dice debajo de la barra. Sin él, el texto de
   * siempre («Se cumplen todas…»), que es verdad cuando lo que queda está dentro
   * del tramo verde; el aviso de un precio escrito a mano que no cumple pone el suyo.
   */
  pie?: string;
}) {
  const { desde, hasta } = ejeDe(rangos, marcas);
  const fraccion = (v: number) => (v - desde) / (hasta - desde);
  const pct = (v: number) => `${fraccion(v) * 100}%`;
  const conFranja = rangos.filter((r) => r.aviso == null && (r.min != null || r.max != null));
  const sinDato = rangos.filter((r) => r.aviso != null);
  // Ninguna regla pone un límite medible: no hay cruce que dibujar (no es «todo permitido»).
  const sinLimites = conFranja.length === 0;
  const textoCruce = cruce.vacio
    ? 'conflicto entre reglas'
    : `permitido de ${cruce.min != null ? pisoEnTexto(cruce.min) : 'sin piso'} a ${cruce.max != null ? techoEnTexto(cruce.max) : 'sin techo'}`;

  const franja = (min: number | null, max: number | null) => ({
    left: pct(min ?? desde),
    width: `calc(${pct(max ?? hasta)} - ${pct(min ?? desde)})`,
  });

  // A5: el tramo verde angosto lleva sus números afuera, del lado con más lugar.
  // Lote B2 (detalle del lote A): sin techo, «desde 3.242,36»; sin piso, «hasta …»
  // (con «3.242,36 – …» parecía cortado).
  const numerosDelCruce =
    cruce.min != null && cruce.max != null
      ? `${pisoEnTexto(cruce.min)} – ${techoEnTexto(cruce.max)}`
      : cruce.min != null
        ? `desde ${pisoEnTexto(cruce.min)}`
        : cruce.max != null
          ? `hasta ${techoEnTexto(cruce.max)}`
          : '';
  const inicioCruce = fraccion(cruce.min ?? desde);
  const finCruce = fraccion(cruce.max ?? hasta);
  const cruceAngosto = finCruce - inicioCruce < CRUCE_ANGOSTO;
  const ladoDelRotulo = inicioCruce > 1 - finCruce ? 'izquierda' : 'derecha';

  // Clases literales: Tailwind no genera clases armadas con replace().
  // «queda» va debajo de la barra; «hoy», «pide» y «costo» arriba, en filas que no
  // se pisan (filasDeEtiquetas).
  const deArriba = [
    { clave: 'hoy', v: marcas.hoy, linea: 'bg-marca-hoy', texto: 'text-marca-hoy' },
    { clave: 'pide', v: marcas.pedido, linea: 'bg-marca-pide', texto: 'text-marca-pide' },
    { clave: 'costo', v: marcas.costo, linea: 'bg-marca-costo', texto: 'text-marca-costo' },
  ].filter((m): m is typeof m & { v: number } => m.v != null);
  const filaDe = filasDeEtiquetas(
    deArriba.map((m) => ({ clave: m.clave, pos: (m.v - desde) / (hasta - desde) }))
  );
  const nFilas = deArriba.length === 0 ? 0 : Math.max(...Object.values(filaDe)) + 1;

  const raya = (v: number, clave: string, linea: string) => (
    <div className="absolute top-0 bottom-0" style={{ left: pct(v) }} key={clave}>
      <div className={`h-full w-px ${linea}`} />
    </div>
  );

  if (compacta) {
    return (
      <div
        className="relative h-2 w-full rounded bg-muted"
        role="img"
        aria-label={sinLimites ? 'ninguna regla pone límite' : textoCruce}
      >
        {!cruce.vacio && !sinLimites && (
          <div
            data-testid="cruce"
            className="absolute h-2 rounded bg-rango"
            style={franja(cruce.min, cruce.max)}
          />
        )}
        {cruce.vacio && (
          <div data-testid="hueco" className="absolute inset-0 rounded bg-bloqueo-suave" />
        )}
        {marcas.queda != null && (
          <div
            className="absolute -top-1 h-4 w-0.5 bg-foreground"
            style={{ left: pct(marcas.queda) }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative pt-1 pb-5">
        {/* Una fila por regla que pone rango */}
        <div className="space-y-1.5">
          {conFranja.map((r, i) => (
            <div key={`${r.ruleId}-${i}`} className="grid grid-cols-[220px_1fr] items-center gap-3">
              <div className="truncate text-xs text-muted-foreground" title={r.ruleName}>
                {r.ruleName}
                {sufijoDeAlcance(r.ruleName, alcancePorRegla[r.ruleId]) && (
                  <>
                    {' '}
                    <span className="opacity-70">· {alcancePorRegla[r.ruleId]}</span>
                  </>
                )}
              </div>
              <div className="relative h-3 rounded bg-muted">
                <div
                  data-testid="franja"
                  data-abierta={r.max == null ? 'derecha' : r.min == null ? 'izquierda' : 'no'}
                  className={`absolute h-3 rounded ${r.duro ? 'bg-foreground/70' : 'bg-muted-foreground/50'}`}
                  style={franja(r.min, r.max)}
                />
                {/* A5: la flecha va dentro de la barra, sobre la punta abierta de la franja. */}
                {r.max == null && (
                  <span className="absolute right-0.5 top-1/2 -translate-y-1/2 text-xs leading-none text-background">
                    →
                  </span>
                )}
                {r.min == null && (
                  <span className="absolute left-0.5 top-1/2 -translate-y-1/2 text-xs leading-none text-background">
                    ←
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* El cruce, o el hueco */}
        <div className="mt-2 grid grid-cols-[220px_1fr] items-end gap-3">
          <div className="text-xs font-medium leading-4">
            {cruce.vacio
              ? 'Conflicto entre reglas'
              : sinLimites
                ? 'Ninguna regla pone un límite'
                : 'Lo que cumple todo'}
          </div>
          <div>
            {nFilas > 0 && (
              <div className="relative" style={{ height: nFilas * ALTO_FILA }}>
                {deArriba.map((m) => (
                  <div
                    key={m.clave}
                    data-fila={filaDe[m.clave]}
                    className={`absolute ${anclaDelRotulo(fraccion(m.v))} whitespace-nowrap text-xs font-medium leading-4 ${m.texto}`}
                    style={{ left: pct(m.v), top: (nFilas - 1 - filaDe[m.clave]) * ALTO_FILA }}
                  >
                    {m.clave} {fmt(m.v)}
                  </div>
                ))}
              </div>
            )}
            <div
              className="relative h-4 rounded bg-muted"
              role="img"
              aria-label={sinLimites ? 'ninguna regla pone límite' : textoCruce}
            >
              {!cruce.vacio && !sinLimites && (
                <div
                  data-testid="cruce"
                  className="absolute flex h-4 items-center justify-center rounded bg-rango text-xs font-medium text-background"
                  style={franja(cruce.min, cruce.max)}
                >
                  {!cruceAngosto && numerosDelCruce}
                </div>
              )}
              {!cruce.vacio && !sinLimites && cruceAngosto && (
                <div
                  data-testid="cruce-rotulo"
                  data-lado={ladoDelRotulo}
                  className={`absolute top-0 flex h-4 items-center whitespace-nowrap text-xs font-medium text-foreground ${ladoDelRotulo === 'izquierda' ? 'pr-1' : 'pl-1'}`}
                  style={
                    ladoDelRotulo === 'izquierda'
                      ? { right: `${(1 - inicioCruce) * 100}%` }
                      : { left: `${finCruce * 100}%` }
                  }
                >
                  {numerosDelCruce}
                </div>
              )}
              {cruce.vacio && cruce.min != null && cruce.max != null && (
                <div
                  data-testid="hueco"
                  className="absolute h-4 rounded border border-dashed border-bloqueo bg-bloqueo-suave"
                  style={franja(cruce.max, cruce.min)}
                />
              )}
              {deArriba.map((m) => raya(m.v, m.clave, m.linea))}
              {marcas.queda != null && (
                <div className="absolute top-0 bottom-0" style={{ left: pct(marcas.queda) }}>
                  <div className="h-full w-px bg-foreground" />
                  <div
                    className={`absolute -bottom-5 ${anclaDelRotulo(fraccion(marcas.queda))} whitespace-nowrap text-xs font-medium text-foreground`}
                  >
                    queda {fmt(marcas.queda)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Lo que no dibuja franja: sale igual, con el porqué */}
      {sinDato.length > 0 && (
        <ul className="space-y-0.5 text-xs text-muted-foreground">
          {sinDato.map((r, i) => (
            <li key={`${r.ruleId}-sin-${i}`}>
              <span className="font-medium text-foreground">{r.ruleName}</span> — {r.aviso}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-muted-foreground">
        {pie ??
          (cruce.vacio
            ? 'Las reglas no se pueden cumplir todas a la vez: el precio no se mueve solo y espera en Por decidir.'
            : sinLimites
              ? 'Ninguna regla le pone un límite a este producto: queda lo que pide la estrategia.'
              : COMO_SE_COMBINAN)}
      </p>

      {/* Las salidas vienen como lista y en orden del backend: MARGEN·OTRAS·NO_MOVER,
          o una por regla (REGLA:<id>) + NO_MOVER. Pueden ser 3 o más. */}
      {salidas && salidas.length > 0 && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-2 text-xs">
          {salidas.map((s) => (
            <div key={s.clave} data-testid="salida" className="rounded border p-2">
              <div className="font-medium">{fmt(s.precio)}</div>
              <div className="text-muted-foreground">{s.explicacion}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
